import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { HiChartPie, HiTrendingUp } from 'react-icons/hi';
import { formatMoney, formatDate, categoryColor } from '../../utils/reportFormat';

const MAX_SLICES = 7;

const tooltipStyle = {
  backgroundColor: '#fff',
  border: '1px solid #F0E4D3',
  borderRadius: '12px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
  padding: '10px 14px',
  direction: 'rtl',
  fontSize: 12,
};

function Panel({ icon: Icon, title, subtitle, children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl shadow-sm border border-primary-100/40 p-5 animate-fadeIn ${className}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2.5 rounded-xl bg-primary-50 text-primary-600">
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-800">{title}</h3>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function CategoryCharts({ categories, dailyTrend, totalRevenue }) {
  // Long tails are folded into "أخرى" so the donut stays readable.
  const slices = categories.slice(0, MAX_SLICES).map((c, i) => ({ name: c.name, value: c.revenue, color: categoryColor(i) }));
  const rest = categories.slice(MAX_SLICES);
  if (rest.length) {
    slices.push({ name: `أخرى (${rest.length})`, value: rest.reduce((s, c) => s + c.revenue, 0), color: '#D1D5DB' });
  }

  const trend = dailyTrend.map((d) => ({ ...d, label: formatDate(d.date, { month: 'short', day: 'numeric' }) }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <Panel icon={HiChartPie} title="توزيع الإيرادات" subtitle="حصة كل تصنيف من الإجمالي" className="lg:col-span-2">
        <div className="relative h-56">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={slices}
                dataKey="value"
                nameKey="name"
                innerRadius="62%"
                outerRadius="92%"
                paddingAngle={slices.length > 1 ? 2 : 0}
                stroke="none"
              >
                {slices.map((s) => <Cell key={s.name} fill={s.color} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [formatMoney(v), n]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-[11px] text-gray-400">الإجمالي</span>
            <span className="text-base font-bold text-gray-800" dir="ltr">{formatMoney(totalRevenue)}</span>
          </div>
        </div>
        <ul className="mt-4 space-y-2">
          {slices.map((s) => {
            const pct = totalRevenue > 0 ? ((s.value / totalRevenue) * 100).toFixed(1) : '0.0';
            return (
              <li key={s.name} className="flex items-center gap-2 text-sm">
                <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: s.color }} />
                <span className="flex-1 truncate text-gray-600">{s.name}</span>
                <span className="text-xs text-gray-400">{pct}%</span>
                <span className="font-semibold text-gray-800 w-28 text-left" dir="ltr">{formatMoney(s.value)}</span>
              </li>
            );
          })}
        </ul>
      </Panel>

      <Panel icon={HiTrendingUp} title="اتجاه المبيعات" subtitle="الإيرادات والربح التقديري يومياً" className="lg:col-span-3">
        <div className="flex items-center gap-4 text-xs mb-2">
          <span className="flex items-center gap-1.5 text-gray-500">
            <span className="w-2.5 h-2.5 rounded-full bg-primary-500" /> الإيرادات
          </span>
          <span className="flex items-center gap-1.5 text-gray-500">
            <span className="w-2.5 h-2.5 rounded-full bg-gold-500" /> الربح التقديري
          </span>
        </div>
        <div className="h-72" dir="ltr">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6B4A2E" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#6B4A2E" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="profitFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D7B032" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#D7B032" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f0ea" vertical={false} />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }} minTickGap={16} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }} width={48} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v, n) => [formatMoney(v), n === 'revenue' ? 'الإيرادات' : 'الربح التقديري']}
                labelFormatter={(_, p) => (p?.[0] ? formatDate(p[0].payload.date) : '')}
              />
              <Area type="monotone" dataKey="revenue" stroke="#6B4A2E" strokeWidth={2.5} fill="url(#revFill)" />
              <Area type="monotone" dataKey="profit" stroke="#D7B032" strokeWidth={2} fill="url(#profitFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Panel>
    </div>
  );
}
